import { ExecutivePower, type GameView, type PolicyType, type PublicGameState } from '@secretnenshi/shared';
import { useTheme } from '../theme/useTheme';

function Track({ label, filled, labels, notes }: { label: string; filled: number; labels: readonly string[]; notes?: string[] }) {
  return (
    <div className="track">
      <div className="track-head">
        <span className="section-label">{label}</span>
        <span className="track-count">
          {filled} / {labels.length}
        </span>
      </div>
      <ol className="track-slots" aria-label={`${label}: ${filled} of ${labels.length}`}>
        {labels.map((l, i) => (
          <li key={l + i} className={`slot ${i < filled ? 'filled' : ''}`}>
            <span className="slot-mark" aria-hidden="true" />
            {/* Long words shrink to fit instead of breaking mid-word. */}
            <span className={`slot-label ${l.split(/\s+/).some((w) => w.length > 7) ? 'slot-label-long' : ''}`}>{l}</span>
            {notes?.[i] ? <span className="slot-note">{notes[i]}</span> : null}
          </li>
        ))}
      </ol>
    </div>
  );
}

export function Tracks({ game }: { game: PublicGameState }) {
  const c = useTheme();
  const rules = c.rules(game.players.length);
  const powers = rules.federalistTrack.map((s) =>
    s.power === ExecutivePower.NONE || s.power === ExecutivePower.FEDERALIST_VICTORY ? '' : c.powerShort(s.power),
  );
  // Mark the hidden leader's threshold on the minority's track.
  const notes = powers.map((p, i) => (i + 1 === rules.nenshiThreshold ? [p, c.t.leader.name].filter(Boolean).join(' · ') : p));
  return (
    <div className="board">
      <Track label={c.t.policies.SEPARATION.short} filled={game.separationPolicyCount} labels={c.t.policies.SEPARATION.track} />
      <Track label={c.t.policies.FEDERALIST.short} filled={game.federalistPolicyCount} labels={c.t.policies.FEDERALIST.track} notes={notes} />
      <div className="gridlock">
        <span className="section-label">{c.t.phases.gridlock.short}</span>
        <span className="dots" aria-label={`${c.t.phases.gridlock.short} ${game.gridlockCount} of ${rules.gridlockLimit}`}>
          {Array.from({ length: rules.gridlockLimit }, (_, i) => (
            <span key={i} className={`dot ${i < game.gridlockCount ? 'filled' : ''}`} />
          ))}
        </span>
        <span className="pile-counts">
          Deck {game.drawPileCount} · Discard {game.discardPileCount}
        </span>
      </div>
    </div>
  );
}

export function Offices({ view }: { view: GameView }) {
  const c = useTheme();
  const game = view.game!;
  const name = (id?: string) => view.players.find((p) => p.id === id)?.name ?? '—';
  return (
    <dl className="offices">
      <div>
        <dt>{c.minister}</dt>
        <dd>
          {name(game.currentMinisterPlayerId)}
          {game.unityGovernment ? <span className="tag"> ({c.t.powers.UNITY_GOVERNMENT.short})</span> : null}
        </dd>
      </div>
      <div>
        <dt>{c.premier}</dt>
        <dd>{name(game.nominatedPremierPlayerId)}</dd>
      </div>
    </dl>
  );
}

export function PlayerList({ view, me }: { view: GameView; me?: string }) {
  const c = useTheme();
  const game = view.game!;
  const voted = new Set(game.votedPlayerIds);
  const acked = new Set(game.roleAcknowledgedPlayerIds);
  const eligible = new Set(game.eligiblePremierPlayerIds);
  return (
    <ol className="roster compact">
      {view.players.map((p) => {
        const pub = game.players.find((x) => x.id === p.id);
        const badges = [
          p.id === game.currentMinisterPlayerId ? c.minister : null,
          p.id === game.nominatedPremierPlayerId ? c.premier : null,
          game.phase === 'NOMINATE_PREMIER' && pub?.active && p.id !== game.currentMinisterPlayerId && !eligible.has(p.id)
            ? 'Ineligible'
            : null,
          pub && !pub.active ? 'Out of play' : null,
          game.phase === 'CONFIDENCE_VOTE' && voted.has(p.id) ? 'Voted' : null,
          game.phase === 'ROLE_REVEAL' && acked.has(p.id) ? 'Ready' : null,
        ].filter(Boolean);
        return (
          <li key={p.id} className={`roster-row ${pub && !pub.active ? 'inactive' : ''}`}>
            <span className={`presence ${p.connected ? 'on' : 'off'}`} aria-label={p.connected ? 'online' : 'offline'} />
            <span className="roster-name">
              {p.name}
              {p.id === me ? <span className="tag"> (you)</span> : null}
            </span>
            <span className="badges">{badges.join(' · ')}</span>
          </li>
        );
      })}
    </ol>
  );
}

export function PolicyCard({
  policy,
  index,
  selected,
  onSelect,
  disabled,
}: {
  policy: PolicyType;
  index: number;
  selected?: boolean;
  onSelect?: () => void;
  disabled?: boolean;
}) {
  const c = useTheme();
  const headlines = c.t.policies[policy].headlines;
  return (
    <button
      type="button"
      className={`policy policy-${policy.toLowerCase()} ${selected ? 'selected' : ''}`}
      onClick={onSelect}
      disabled={disabled || !onSelect}
      aria-pressed={selected}
    >
      <span className="policy-type">{c.policy(policy)}</span>
      <span className="policy-headline">{headlines[index % headlines.length]}</span>
    </button>
  );
}
